'use client';

import type React from 'react';
import { useState } from 'react';

interface DevBannerProps {
	name?: string;
	path?: string;
	logsUrl?: string;
}

const BANNER_STYLE: React.CSSProperties = {
	alignItems: 'center',
	background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
	borderRadius: '14px 14px 0 0',
	borderTop: '2px solid #0f3460',
	bottom: 0,
	boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)',
	color: '#e0e0e0',
	display: 'flex',
	fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
	fontSize: '12px',
	gap: '12px',
	left: '50%',
	maxWidth: '500px',
	padding: '6px 12px',
	position: 'fixed',
	transform: 'translateX(-50%)',
	width: 'max-content',
	zIndex: 99_999,
};

const LINK_STYLE: React.CSSProperties = {
	color: '#64b5f6',
	textDecoration: 'none',
};

const COLLAPSE_BTN_STYLE: React.CSSProperties = {
	background: 'none',
	border: 'none',
	color: '#e0e0e0',
	cursor: 'pointer',
	fontSize: '14px',
	padding: '0 4px',
};

const CLICKABLE_NAME_STYLE: React.CSSProperties = {
	background: 'none',
	border: 'none',
	color: '#e0e0e0',
	cursor: 'copy',
	fontFamily: 'inherit',
	fontSize: 'inherit',
	fontWeight: 'bold',
	maxWidth: '250px',
	overflow: 'hidden',
	padding: 0,
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
};

const ICON_BTN_STYLE: React.CSSProperties = {
	background: 'none',
	border: 'none',
	color: '#888',
	cursor: 'copy',
	flexShrink: 0,
	fontSize: '13px',
	lineHeight: 1,
	padding: '0 2px',
};

export function DevBanner({ logsUrl, name, path }: DevBannerProps) {
	const [collapsed, setCollapsed] = useState(false);

	if (!name) return null;

	if (collapsed) {
		return (
			<div style={{ ...BANNER_STYLE, gap: '0px', padding: '4px 8px' }}>
				<button
					aria-label={`Show dev banner for ${name}`}
					onClick={() => setCollapsed(false)}
					style={{ ...COLLAPSE_BTN_STYLE, fontSize: '16px' }}
					title={name}
					type="button"
				>
					&#9741;
				</button>
			</div>
		);
	}

	return (
		<div style={BANNER_STYLE}>
			<span style={{ flexShrink: 0, fontSize: '16px' }} title="Git worktree">
				&#9741;
			</span>
			<button
				onClick={() => void navigator.clipboard.writeText(name)}
				style={CLICKABLE_NAME_STYLE}
				title={`${name}\n(click to copy)`}
				type="button"
			>
				{name}
			</button>
			{path && (
				<button
					onClick={() => void navigator.clipboard.writeText(path)}
					style={ICON_BTN_STYLE}
					title={`Copy path: ${path}`}
					type="button"
				>
					&#128203;
				</button>
			)}
			{logsUrl && (
				<a href={logsUrl} rel="noopener noreferrer" style={{ ...LINK_STYLE, flexShrink: 0 }} target="_blank">
					logs
				</a>
			)}
			<button
				onClick={() => setCollapsed(true)}
				style={{ ...COLLAPSE_BTN_STYLE, flexShrink: 0 }}
				title="Collapse dev banner"
				type="button"
			>
				&#9660;
			</button>
		</div>
	);
}
